import React, { useEffect, useRef, useState } from "react";
import { participantName } from "../../backend/api/chat";
import type { ChatConversation } from "../../backend/api/chat";

type IncomingMessageAnnouncerProps = {
  conversations: ChatConversation[] | undefined;
  currentUserId: string | null | undefined;
  titleFor: (conversation: ChatConversation) => string;
};

/**
 * Tells a screen reader that a message arrived.
 *
 * The conversation list is already refreshed whenever the socket delivers a
 * message, and each row carries its newest message, so a change in
 * `last_message_at` from someone else is the signal. The thread itself is not a
 * live region: loading older pages and withdrawing a message would be read out
 * as if they were new. The first list that arrives sets the baseline and is
 * never announced.
 */
export function IncomingMessageAnnouncer({
  conversations,
  currentUserId,
  titleFor,
}: IncomingMessageAnnouncerProps) {
  const seen = useRef<Map<string, string | null> | null>(null);
  const [announcements, setAnnouncements] = useState<
    { id: number; text: string }[]
  >([]);
  const counter = useRef(0);

  useEffect(() => {
    if (!conversations) return;
    const previous = seen.current;
    seen.current = new Map(
      conversations.map((conversation) => [
        conversation.id,
        conversation.last_message_at,
      ]),
    );
    if (!previous) return;

    const fresh = conversations.filter(
      (conversation) =>
        conversation.last_message_at &&
        conversation.last_message_at !== previous.get(conversation.id) &&
        conversation.last_message_sender_id !== currentUserId,
    );
    if (!fresh.length) return;

    const lines = fresh.map((conversation) => {
      const sender = conversation.participants?.find(
        (participant) =>
          participant.user_id === conversation.last_message_sender_id,
      );
      const who = sender ? participantName(sender) : "Someone";
      const preview = conversation.last_message_preview;
      return `New message from ${who} in ${titleFor(conversation)}${
        preview ? `: ${preview}` : ""
      }`;
    });
    counter.current += 1;
    setAnnouncements([{ id: counter.current, text: lines.join(". ") }]);
  }, [conversations, currentUserId, titleFor]);

  // A fresh node per announcement, so the same words twice are read twice.
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      className="sr-only"
    >
      {announcements.map((announcement) => (
        <p key={announcement.id}>{announcement.text}</p>
      ))}
    </div>
  );
}
